/**
 * ACTIVITY-CORE — deep history (beyond local retention ~90 days)
 * paginated from API: `GET /api/activity?before=&limit=`.
 *
 * NOTE (untyped fallback): endpoint is not yet in generated types of
 * `@mealplan/client` (requires `make rust-gen-api-types`). Uses untyped
 * fetch with cookie-BFF web transport and DEFENSIVELY parses the
 * response (server camelCase → local snake_case shape), never throws on
 * missing fields. When regenerating types, migrate to `api.GET("/api/activity")`.
 */
import type { ActivityRow } from "@mealplan/shared";
import { SERVER_BASE_URL } from "@/lib/api";
import { readActiveAccountSelector } from "@/lib/accounts";

interface OlderActivityPage {
  rows: ActivityRow[];
  nextBefore: string | null;
}

function str(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

/** Assertion-free type guard: indexable plain object. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

/** API row (camelCase, tolerant) → local snake_case shape of feed. */
function mapApiRow(item: Record<string, unknown>): ActivityRow {
  const { patch } = item;
  return {
    id: str(item.id) ?? "",
    scope_id: str(item.scopeId),
    list_id: str(item.listId),
    entity_type: str(item.entityType),
    entity_id: str(item.entityId),
    action: str(item.action),
    actor_user_id: str(item.actorUserId),
    actor_name: str(item.actorName),
    actor_device_id: str(item.actorDeviceId),
    platform: str(item.platform),
    server_ts: str(item.serverTs),
    client_ts: str(item.clientTs),
    patch: typeof patch === "string" ? patch : patch == null ? null : JSON.stringify(patch),
    batch_id: str(item.batchId),
    reverts_activity_id: str(item.revertsActivityId),
  };
}

export async function loadOlderActivity(
  before: string | null,
  limit: number,
): Promise<OlderActivityPage> {
  const headers: Record<string, string> = {};
  const selector = readActiveAccountSelector();
  if (selector) {
    headers["X-Active-Account"] = selector;
  }
  const params = new URLSearchParams({ limit: String(limit) });
  if (before) {
    params.set("before", before);
  }
  const response = await fetch(`${SERVER_BASE_URL}/api/activity?${params.toString()}`, {
    method: "GET",
    credentials: "include",
    headers,
  });
  if (!response.ok) {
    throw new Error(`load activity failed with status ${response.status}`);
  }
  const body: unknown = await response.json().catch(() => null);
  if (!isRecord(body)) {
    return { rows: [], nextBefore: null };
  }
  const items = Array.isArray(body.items) ? body.items : [];
  const rows = items
    .filter((item): item is Record<string, unknown> => isRecord(item))
    .map(mapApiRow)
    .filter((row) => row.id.length > 0);
  return { rows, nextBefore: str(body.nextBefore) };
}
